import { useEffect, useState } from "react";
import BioCard from "../components/BioCard";
import NextPage from "../components/NextPage";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import AdaptivePhoto from "../components/AdaptivePhoto";
import { Butterfly } from "../components/Butterflies";
import { SALTIE_MOOS } from "../lib/data";
import { playSound } from "../lib/audio";
import { getSaltieMoosPage } from "../lib/api";
import useCharacterBios from "../hooks/useCharacterBios";
import { safeHtml } from "../lib/safeHtml";

const DEFAULT_PAGE = {
  seo_title: "Meet the Saltie Moos\u2122",
  seo_description: "Highland cattle from a real Texas pasture \u2014 peaceful strength, gentleness, and quiet confidence.",
  hero_title: "The Saltie Moos\u2122",
  hero_subtitle: "Peaceful strength \u00b7 Gentleness \u00b7 Quiet confidence.",
  intro_body: "Highland cattle from a real Texas pasture. Each one a reminder that strength and gentleness can live together.",
  hear_a_moo_heading: "Hear a Moo. Praise, Too.",
  hear_a_moo_subheading: "Every moo is a gentle reminder to notice God\u2019s goodness.",
  hear_a_moo_body_html:
    "<p>Our Highland cows remind us to slow down, look around, and praise God for the blessings He has placed in our lives.</p><p>Praise does not always happen inside a church. Sometimes it happens beside a pasture fence, beneath an open sky, or while listening to a gentle moo carry across the field.</p><p>A moo may remind us to say:</p><ul><li>\u201cThank You for today.\u201d</li><li>\u201cThank You for my family.\u201d</li><li>\u201cThank You for this pasture.\u201d</li><li>\u201cThank You for Your faithfulness.\u201d</li></ul><p>One little moo can become one moment of praise. And those moments help us notice the goodness God has already placed around us.</p>",
  hear_a_moo_closing: "Hear a Moo. Praise, Too.",
  hear_a_moo_photo_url: "",
  hear_a_moo_photo_alt: "A Highland cow in the pasture",
  next_button_label: "Find a Saltie Wink",
};

export default function SaltieMoos() {
  const moos = useCharacterBios(SALTIE_MOOS);
  const [page, setPage] = useState(DEFAULT_PAGE);
  useEffect(() => {
    playSound("/audio/saltie-moo.mp3", 0.9);
    getSaltieMoosPage()
      .then((p) => setPage({ ...DEFAULT_PAGE, ...(p || {}) }))
      .catch(() => {});
  }, []);
  return (
    <div className="parchment min-h-screen" data-testid="saltie-moos-page">
      <SEO title={page.seo_title} description={page.seo_description} path="/saltie-moos" />
      <ChapterHero
        path="/saltie-moos"
        image="/assets/heroes/saltie-moos.webp"
        title={page.hero_title}
        subtitle={page.hero_subtitle}
      />
      <div className="max-w-6xl mx-auto px-6 -mt-8 relative z-10 pb-12">
        <p className="font-body text-lg text-[#2C2724]/85 max-w-2xl mx-auto text-center leading-relaxed mb-14">
          {page.intro_body}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {moos.map((b, i) => {
            const tints = ["#579DE6", "#2DE181", "#4B3A2F", "#C8A876", "#4B3A2F"];
            return (
              <BioCard
                key={b.slug}
                slug={b.slug}
                name={b.name}
                role={b.role}
                intro={b.intro}
                photoLabel={b.name}
                accent={tints[i % tints.length]}
              />
            );
          })}
        </div>
      </div>

      {/* ── Hear a Moo. Praise, Too. ───────────────────────────── */}
      <section
        data-testid="hear-a-moo-section"
        className="relative max-w-6xl mx-auto px-6 pb-24 pt-6"
      >
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          {/* Text LEFT on desktop; but photo stacks first (above) on mobile */}
          <div className="order-2 md:order-1 text-center md:text-left">
            <div className="flex md:justify-start justify-center mb-3 opacity-90">
              <Butterfly />
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#4B3A2F] leading-tight">
              {page.hear_a_moo_heading}
            </h2>
            {page.hear_a_moo_subheading && (
              <p className="mt-3 font-script text-2xl md:text-3xl text-[#7E9268]">
                {page.hear_a_moo_subheading}
              </p>
            )}
            <div className="gold-rule my-6 mx-auto md:mx-0 w-24" />
            <div
              className="font-body text-lg text-[#2C2724]/90 leading-relaxed prose-winks text-left"
              dangerouslySetInnerHTML={safeHtml(page.hear_a_moo_body_html)}
            />
            {page.hear_a_moo_closing && (
              <p className="mt-8 font-display italic text-2xl md:text-3xl text-[#4B3A2F]">
                {page.hear_a_moo_closing}
              </p>
            )}
          </div>

          {/* Photo RIGHT on desktop; stacks first on mobile */}
          <div className="order-1 md:order-2">
            <AdaptivePhoto
              testId="hear-a-moo-photo"
              src={page.hear_a_moo_photo_url}
              alt={page.hear_a_moo_photo_alt}
              frameColor="#7E9268"
              tilt="0.5deg"
              placeholder="cow photo coming soon"
            />
          </div>
        </div>
      </section>

      <NextPage to="/saltie-winks" label={page.next_button_label} />
    </div>
  );
}
